import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, Scissors, DollarSign, CheckCircle } from 'lucide-react';
import { format, addMinutes } from 'date-fns';
import { he } from 'date-fns/locale';

interface AppointmentSummaryProps {
  data: {
    customerName: string;
    customerPhone: string;
    customerEmail?: string;
    serviceId: string;
    serviceName: string;
    servicePrice: number;
    serviceDuration: string;
    servicePromotion?: any;
    serviceImageUrl?: string;
    staffId: string;
    staffName: string;
    date: Date;
  };
  onEdit: (field: 'customer' | 'service' | 'datetime') => void;
  onConfirm: () => void;
  loading?: boolean;
}

export function AppointmentSummary({ data, onEdit, onConfirm, loading }: AppointmentSummaryProps) {
  const getDurationMinutes = (duration: string): number => {
    const intervalMatch = duration.match(/(\d+):(\d+):(\d+)/);
    if (intervalMatch) {
      const [_, hours, minutes, seconds] = intervalMatch;
      if (parseInt(hours) > 0 || parseInt(minutes) > 0) {
        return parseInt(hours) * 60 + parseInt(minutes);
      } else if (parseInt(seconds) > 0) {
        return parseInt(seconds);
      }
    }

    const textMatch = duration.match(/(\d+)\s*(minute|hour)s?/);
    if (textMatch) {
      const [_, value, unit] = textMatch;
      return unit === 'hour' ? parseInt(value) * 60 : parseInt(value);
    }

    const numericMatch = duration.match(/^(\d+)$/);
    if (numericMatch) {
      return parseInt(numericMatch[1]);
    }

    return 30;
  };

  const durationMinutes = getDurationMinutes(data.serviceDuration);
  const endTime = addMinutes(data.date, durationMinutes);

  // חישוב מחיר עם מבצע
  const promotion = data.servicePromotion;
  const hasPromotion = promotion?.is_active && promotion?.discount_value;

  let displayPrice = data.servicePrice;
  if (hasPromotion && promotion) {
    if (promotion.discount_type === 'percentage') {
      displayPrice = data.servicePrice * (1 - (promotion.discount_value / 100));
    } else {
      displayPrice = data.servicePrice - promotion.discount_value;
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {/* Service Image */}
        {data.serviceImageUrl && (
          <div className="flex justify-center">
            <img
              src={data.serviceImageUrl}
              alt={data.serviceName}
              className="w-24 h-24 object-cover"
              style={{ borderRadius: '1rem' }}
            />
          </div>
        )}

        {/* Service Name */}
        <div className="text-center">
          <h3 className="text-2xl font-bold" style={{ color: '#1C1C1E' }}>
            {data.serviceName}
          </h3>
        </div>

        {/* Details Card */}
        <div className="bg-white border rounded-2xl overflow-hidden" style={{ borderColor: '#E5E7EB' }}>
          {/* Customer */}
          <button
            onClick={() => onEdit('customer')}
            className="w-full flex items-center gap-4 px-4 py-3 border-b text-right"
            style={{ borderColor: '#F3F4F6' }}
          >
            <div className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#F3F4F6' }}>
              <User className="h-5 w-5" style={{ color: '#007AFF' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs" style={{ color: '#8E8E93' }}>לקוח</p>
              <p className="font-semibold truncate" style={{ color: '#1C1C1E' }}>{data.customerName}</p>
              <p className="text-sm truncate" style={{ color: '#8E8E93' }}>{data.customerPhone}</p>
            </div>
            <div className="text-sm font-medium" style={{ color: '#007AFF' }}>
              ערוך
            </div>
          </button>

          {/* Date & Time */}
          <button
            onClick={() => onEdit('datetime')}
            className="w-full flex items-center gap-4 px-4 py-3 border-b text-right"
            style={{ borderColor: '#F3F4F6' }}
          >
            <div className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#F3F4F6' }}>
              <Calendar className="h-5 w-5" style={{ color: '#007AFF' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs" style={{ color: '#8E8E93' }}>מועד</p>
              <p className="font-semibold" style={{ color: '#1C1C1E' }}>
                {format(data.date, 'EEEE, d בMMMM', { locale: he })}
              </p>
              <p className="text-sm" style={{ color: '#8E8E93' }}>
                {format(data.date, 'HH:mm')} - {format(endTime, 'HH:mm')}
              </p>
            </div>
            <div className="text-sm font-medium" style={{ color: '#007AFF' }}>
              ערוך
            </div>
          </button>

          {/* Staff */}
          <button
            onClick={() => onEdit('datetime')}
            className="w-full flex items-center gap-4 px-4 py-3 border-b text-right"
            style={{ borderColor: '#F3F4F6' }}
          >
            <div className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#F3F4F6' }}>
              <Scissors className="h-5 w-5" style={{ color: '#007AFF' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs" style={{ color: '#8E8E93' }}>איש צוות</p>
              <p className="font-semibold" style={{ color: '#1C1C1E' }}>{data.staffName}</p>
            </div>
            <div className="text-sm font-medium" style={{ color: '#007AFF' }}>
              ערוך
            </div>
          </button>

          {/* Duration */}
          <div className="flex items-center gap-4 px-4 py-3 border-b" style={{ borderColor: '#F3F4F6' }}>
            <div className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#F3F4F6' }}>
              <Clock className="h-5 w-5" style={{ color: '#007AFF' }} />
            </div>
            <div className="flex-1">
              <p className="text-xs" style={{ color: '#8E8E93' }}>משך</p>
              <p className="font-semibold" style={{ color: '#1C1C1E' }}>{durationMinutes} דקות</p>
            </div>
          </div>

          {/* Price */}
          <div className="flex items-center gap-4 px-4 py-3" style={{ backgroundColor: '#F9FAFB' }}>
            <div className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#FFFFFF' }}>
              <DollarSign className="h-5 w-5" style={{ color: '#007AFF' }} />
            </div>
            <div className="flex-1">
              <p className="text-xs" style={{ color: '#8E8E93' }}>מחיר</p>
              <div className="flex items-center gap-2">
                {hasPromotion ? (
                  <>
                    <span className="text-sm line-through" style={{ color: '#8E8E93' }}>
                      ₪{data.servicePrice}
                    </span>
                    <span className="text-2xl font-bold" style={{ color: '#34C759' }}>
                      ₪{displayPrice.toFixed(0)}
                    </span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: '#D1FAE5',
                        color: '#059669',
                      }}
                    >
                      מבצע
                    </span>
                  </>
                ) : (
                  <span className="text-2xl font-bold" style={{ color: '#1C1C1E' }}>
                    ₪{displayPrice}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Button - Fixed at Bottom */}
      <div className="flex-shrink-0 px-6 py-4 border-t bg-white" style={{ borderColor: '#F3F4F6' }}>
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() => {

            onConfirm();
          }}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-4 font-bold rounded-2xl"
          style={{
            backgroundColor: loading ? '#8E8E93' : '#007AFF',
            color: '#FFFFFF',
          }}
        >
          {loading ? (
            <>
              <div
                className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: '#FFFFFF' }}
              />
              <span>מאשר...</span>
            </>
          ) : (
            <>
              <CheckCircle className="h-5 w-5" />
              <span>אשר וקבע תור</span>
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
}
